// testAPI.ts
import {ApiSourceFormData, TestAPIResult, ApiResponseData, ApiResponseValue} from '../types';
import { testApiEndpoint } from './apiService';

export const parseApiResponse = (response: ApiResponseData, config: ApiSourceFormData): ApiResponseData => {
    let data: ApiResponseData = response;

    const responseParsing = config.response_parsing || {
        type: 'json',
        path: '',
        array_path: '',
        mapping: {}
    };

    // Follow the data path if specified
    if (responseParsing.path && typeof data === 'object' && data !== null) {
        const pathParts = responseParsing.path.split('.');
        for (const part of pathParts) {
            if (data && typeof data === 'object' && !Array.isArray(data)) {
                data = data[part] as ApiResponseData;
            } else {
                break;
            }
        }
    }

    // Handle array path if specified
    if (responseParsing.array_path && data && typeof data === 'object') {
        const arrayPath = responseParsing.array_path.split('.');
        for (const part of arrayPath) {
            if (data && typeof data === 'object' && !Array.isArray(data)) {
                data = data[part] as ApiResponseData;
            } else {
                break;
            }
        }
    }

    // Apply field mapping if specified
    if (responseParsing.mapping && Object.keys(responseParsing.mapping).length > 0) {
        if (Array.isArray(data)) {
            return {
                items: data.map(item =>
                    mapFields(item as ApiResponseData, responseParsing.mapping)
                )
            };
        } else if (data && typeof data === 'object') {
            return mapFields(data, responseParsing.mapping);
        }
    }

    // Ensure we return an ApiResponseData object
    if (data === null) {
        return { value: null };
    }
    return typeof data === 'object' ? data : { value: data };
};

const mapFields = (data: ApiResponseData, mapping: Record<string, string>): ApiResponseData => {
    const result: ApiResponseData = {};
    if (!data) return result;

    for (const [sourceField, targetField] of Object.entries(mapping)) {
        let objValue: ApiResponseValue = data;
        const keys = sourceField.split('.');

        for (const key of keys) {
            if (objValue && typeof objValue === 'object' && !Array.isArray(objValue)) {
                const nextValue: ApiResponseValue = (objValue as Record<string, ApiResponseValue>)[key];
                objValue = nextValue ?? null;
            } else {
                objValue = null;
                break;
            }
        }

        if (objValue !== null) {
            result[targetField] = objValue;
        }
    }

    return result;
};

export const testAPI = async (formData: ApiSourceFormData): Promise<TestAPIResult> => {
    try {
        if (!formData.endpoint) {
            throw new Error('Endpoint URL is required');
        }

        const timeoutMs = formData.config?.timeout_seconds ? formData.config.timeout_seconds * 1000 : 30000;

        const response = await testApiEndpoint({
            ...formData,
            timeout_ms: timeoutMs
        });

        let parsedData: ApiResponseData;
        try {
            parsedData = parseApiResponse(response.data, formData);
        } catch (parseError) {
            return {
                success: true,
                message: `API call successful, but response parsing failed: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`,
                data: response.data,
                rawResponse: response
            } satisfies TestAPIResult;
        }

        if (formData.response_parsing.array_path && !(parsedData && Array.isArray(parsedData))) {
            return {
                success: false,
                message: 'API call successful, but the specified array path did not return an array.',
                data: parsedData,
                rawResponse: response
            } satisfies TestAPIResult;
        }

        return {
            success: true,
            message: 'API test successful! Response parsed correctly.',
            data: parsedData,
            rawResponse: response
        } satisfies TestAPIResult;
    } catch (error) {
        const errorResult: TestAPIResult = {
            success: false,
            message: error instanceof Error ? error.message : 'An unknown error occurred',
            data: {},
            rawResponse: {
                data: {},
                headers: {},
                status: 0
            }
        };
        return errorResult;
    }
};

export const formatTestResult = (result: TestAPIResult): string => {
    if (!result.success) {
        return `Error: ${result.message}`;
    }

    let message = result.message;

    if (result.rawResponse) {
        const { status, headers } = result.rawResponse;
        message += `\nStatus: ${status}`;

        // Add useful headers info
        const contentType = headers['content-type'];
        if (contentType) {
            message += `\nContent-Type: ${contentType}`;
        }
    }

    return message;
};